import React, { useState, useEffect, useMemo } from 'react';
import { getIconForType } from './icons';

function flatten(node, level, expandedIds, out, parentIds) {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  out.push({
    id: node.id,
    name: node.name,
    type: node.type,
    level: level,
    hasChildren: hasChildren,
    isExpanded: isExpanded,
    parentIds: parentIds,
  });
  if (hasChildren && isExpanded) {
    const newParents = parentIds.concat(node.id);
    for (let i = 0; i < node.children.length; i++) {
      flatten(node.children[i], level + 1, expandedIds, out, newParents);
    }
  }
}

function findChain(node, targetId, chain) {
  if (node.id === targetId) return chain;
  if (!node.children) return null;
  for (let i = 0; i < node.children.length; i++) {
    const res = findChain(node.children[i], targetId, chain.concat(node.id));
    if (res) return res;
  }
  return null;
}

function TreeView(props) {
  const { treeData, selectedId, onSelect, onContextMenu } = props;
  const [expandedIds, setExpandedIds] = useState(() => new Set(['root', 'domain']));

  useEffect(() => {
    if (!selectedId) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.add(selectedId);
      const chain = findChain(treeData, selectedId, []);
      if (chain) chain.forEach((p) => next.add(p));
      return next;
    });
  }, [selectedId, treeData]);

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const rows = useMemo(() => {
    const out = [];
    flatten(treeData, 0, expandedIds, out, []);
    return out;
  }, [treeData, expandedIds]);

  return (
    <div
      className="h-full w-full bg-white overflow-auto"
      style={{ fontFamily: 'Tahoma, Segoe UI, sans-serif' }}
    >
      {rows.map((row) => {
        const isSelected = selectedId === row.id;
        const rowCls =
          'flex items-center cursor-default h-[18px] text-[12px] ' +
          (isSelected ? 'bg-[#316AC5] text-white' : 'hover:bg-[#E8F0FE] text-black');
        const padStyle = { paddingLeft: row.level * 16 + 2 + 'px' };
        return (
          <div
            key={row.id}
            className={rowCls}
            style={padStyle}
            onClick={() => {
              onSelect(row.id);
              if (row.hasChildren && !row.isExpanded) toggleExpand(row.id);
            }}
            onDoubleClick={() => row.hasChildren && toggleExpand(row.id)}
            onContextMenu={(e) => {
              e.stopPropagation();
              onSelect(row.id);
              if (onContextMenu) onContextMenu(e, row.id);
            }}
          >
            <span
              onClick={(e) => {
                e.stopPropagation();
                if (row.hasChildren) toggleExpand(row.id);
              }}
              className="inline-flex items-center justify-center w-[13px] h-[13px] cursor-pointer"
            >
              {row.hasChildren && row.isExpanded && (
                <svg width="9" height="9" viewBox="0 0 9 9">
                  <path d="M1 3h7L4.5 7z" fill={isSelected ? '#FFF' : '#555'} />
                </svg>
              )}
              {row.hasChildren && !row.isExpanded && (
                <svg width="9" height="9" viewBox="0 0 9 9">
                  <path d="M2 1v7l5-3.5z" fill="none" stroke={isSelected ? '#FFF' : '#555'} strokeWidth="1" />
                </svg>
              )}
            </span>
            <span className="mr-[4px] flex items-center">{getIconForType(row.type, 14)}</span>
            <span className="truncate pr-2">{row.name}</span>
          </div>
        );
      })}
    </div>
  );
}

export default TreeView;
